import { create } from 'zustand'

export type Page =
  | 'dashboard'
  | 'clienti'
  | 'contracte'
  | 'echipamente'
  | 'rezervari'
  | 'predare-retur'
  | 'mentenanta'
  | 'facturare'
  | 'incasari'
  | 'cheltuieli'
  | 'transfer'
  | 'hardware'
  | 'rapoarte'
  | 'setari'

export interface NavEntry {
  page: Page
  params: Record<string, string>
}

interface NavState {
  page: Page
  params: Record<string, string>
  history: NavEntry[]
  historyIndex: number
  navGuard: (() => boolean) | null
  blockedNav: { page: Page; params?: Record<string, string> } | null

  navigate: (page: Page, params?: Record<string, string>) => void
  back: () => void
  forward: () => void
  canGoBack: () => boolean
  canGoForward: () => boolean
  setNavGuard: (guard: (() => boolean) | null) => void
  clearBlockedNav: () => void
}

const PAGINI_VALIDE: Page[] = [
  'dashboard', 'clienti', 'contracte', 'echipamente', 'rezervari', 'predare-retur',
  'mentenanta', 'facturare', 'incasari', 'cheltuieli', 'transfer', 'hardware', 'rapoarte', 'setari',
]

/** Deep-link din email: parsează `#/pagina?cheie=valoare` la încărcare. */
function navDinUrl(): NavEntry {
  const implicit: NavEntry = { page: 'dashboard', params: {} }
  if (typeof window === 'undefined') return implicit
  const m = /^#\/([a-z-]+)(?:\?(.*))?$/.exec(window.location.hash)
  if (!m || !PAGINI_VALIDE.includes(m[1] as Page)) return implicit
  const params: Record<string, string> = {}
  if (m[2]) for (const kv of m[2].split('&')) {
    const [k, v] = kv.split('=')
    if (k) params[decodeURIComponent(k)] = decodeURIComponent(v ?? '')
  }
  // Curăță hash-ul ca refresh-ul să nu re-declanșeze (starea persistă prin login).
  try { window.history.replaceState(null, '', window.location.pathname + window.location.search) } catch { /* ignore */ }
  return { page: m[1] as Page, params }
}

const navInitial = navDinUrl()

export const useNav = create<NavState>((set, get) => ({
  page: navInitial.page,
  params: navInitial.params,
  history: [{ page: navInitial.page, params: navInitial.params }],
  historyIndex: 0,
  navGuard: null,
  blockedNav: null,

  setNavGuard: (guard) => set({ navGuard: guard }),
  clearBlockedNav: () => set({ blockedNav: null }),

  navigate: (page, params = {}) => {
    const { history, historyIndex, navGuard } = get()
    if (navGuard && !navGuard()) {
      set({ blockedNav: { page, params } })
      return
    }
    set({ blockedNav: null })
    const newHistory = history.slice(0, historyIndex + 1)
    const current = newHistory[newHistory.length - 1]
    if (current?.page === page && JSON.stringify(current.params) === JSON.stringify(params)) return
    newHistory.push({ page, params })
    set({ page, params, history: newHistory, historyIndex: newHistory.length - 1 })
  },

  back: () => {
    const { history, historyIndex, navGuard } = get()
    if (navGuard && !navGuard()) {
      const newIndex = historyIndex - 1
      if (newIndex >= 0) set({ blockedNav: { page: history[newIndex].page, params: history[newIndex].params } })
      return
    }
    set({ blockedNav: null })
    if (historyIndex <= 0) return
    const newIndex = historyIndex - 1
    const { page, params } = history[newIndex]
    set({ page, params, historyIndex: newIndex })
  },

  forward: () => {
    const { history, historyIndex } = get()
    if (historyIndex >= history.length - 1) return
    const newIndex = historyIndex + 1
    const { page, params } = history[newIndex]
    set({ page, params, historyIndex: newIndex })
  },

  canGoBack: () => get().historyIndex > 0,
  canGoForward: () => get().historyIndex < get().history.length - 1,
}))
